import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { supabase, type Profile, type UserSettings } from './supabase'
import type { User } from '@supabase/supabase-js'
import { logger, setLoggerUserId } from './logger'

interface AuthState {
  user: User | null
  profile: Profile | null
  settings: UserSettings | null
  loading: boolean
  signInWithEmail: (email: string, password: string) => Promise<{ error?: string }>
  signUp: (email: string, password: string) => Promise<{ error?: string }>
  signInWithGoogle: () => Promise<void>
  signInWithGithub: () => Promise<void>
  signInWithTwitter: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  saveProfile: (data: Partial<Profile>) => Promise<void>
  saveSettings: (data: Partial<UserSettings>) => Promise<void>
}

const AuthContext = createContext<AuthState>(null!)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const userId = session?.user?.id || null
      setUser(session?.user ?? null)
      setLoggerUserId(userId)
      logger.info('Auth session checked', { hasSession: !!session })
      if (session?.user) fetchProfile(session.user.id)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const userId = session?.user?.id || null
      setUser(session?.user ?? null)
      setLoggerUserId(userId)
      logger.info('Auth state changed', { event, hasSession: !!session })
      if (session?.user) fetchProfile(session.user.id)
      else {
        setProfile(null)
        setSettings(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId: string) {
    logger.debug('Fetching profile', { userId })
    const { data: p, error: pErr } = await supabase.from('profiles').select('*').eq('id', userId).single()
    if (pErr) logger.warn('Failed to fetch profile', { error: pErr.message, userId })
    setProfile(p as Profile | null)

    const { data: s, error: sErr } = await supabase.from('user_settings').select('*').eq('user_id', userId).single()
    if (sErr && sErr.code !== 'PGRST116') logger.warn('Failed to fetch settings', { error: sErr.message, userId })
    setSettings(s as UserSettings | null)

    if (!s) {
      logger.info('Creating default user settings', { userId })
      const { data: newS, error: insErr } = await supabase.from('user_settings').insert({ user_id: userId }).select().single()
      if (insErr) logger.error('Failed to create default settings', { error: insErr.message, userId })
      setSettings(newS as UserSettings | null)
    }
  }

  async function signInWithEmail(email: string, password: string): Promise<{ error?: string }> {
    logger.info('Email sign-in attempt', { email })
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      logger.warn('Email sign-in failed', { error: error.message, email })
      return { error: error.message }
    }
    logger.info('Email sign-in successful', { email })
    return {}
  }

  async function signUp(email: string, password: string): Promise<{ error?: string }> {
    logger.info('Sign-up attempt', { email })
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin + '/chat' },
    })
    if (error) {
      logger.warn('Sign-up failed', { error: error.message, email })
      return { error: error.message }
    }
    if (!data.session) {
      logger.info('Sign-up confirmation email sent', { email })
      return { error: 'Check your email for a confirmation link, then sign in.' }
    }
    logger.info('Sign-up successful', { email })
    return {}
  }

  async function signInWithGoogle() {
    logger.info('Google sign-in initiated')
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/chat' },
    })
  }

  async function signInWithGithub() {
    logger.info('GitHub sign-in initiated')
    await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: { redirectTo: window.location.origin + '/chat' },
    })
  }

  async function signInWithTwitter() {
    logger.info('Twitter sign-in initiated')
    await supabase.auth.signInWithOAuth({
      provider: 'twitter',
      options: { redirectTo: window.location.origin + '/chat' },
    })
  }

  async function resetPassword(email: string): Promise<{ error?: string }> {
    logger.info('Password reset requested', { email })
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/chat',
    })
    if (error) {
      logger.warn('Password reset failed', { error: error.message, email })
      return { error: error.message }
    }
    logger.info('Password reset email sent', { email })
    return {}
  }

  async function signOut() {
    logger.info('User sign-out')
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    setSettings(null)
  }

  async function saveProfile(data: Partial<Profile>) {
    if (!user) {
      logger.warn('saveProfile called without user')
      return
    }
    logger.info('Saving profile', { data })
    const { data: updated, error } = await supabase.from('profiles').upsert({ id: user.id, ...data }).select().single()
    if (error) {
      logger.error('Failed to save profile', { error: error.message })
      return
    }
    if (updated) setProfile(updated as Profile)
  }

  async function saveSettings(data: Partial<UserSettings>) {
    if (!user) {
      logger.warn('saveSettings called without user')
      return
    }
    logger.info('Saving settings', { keys: Object.keys(data) })
    const { data: updated, error } = await supabase.from('user_settings').upsert({ user_id: user.id, ...data }).select().single()
    if (error) {
      logger.error('Failed to save settings', { error: error.message })
      return
    }
    if (updated) setSettings(updated as UserSettings)
  }

  return (
    <AuthContext.Provider value={{ user, profile, settings, loading, signInWithEmail, signUp, signInWithGoogle, signInWithGithub, signInWithTwitter, resetPassword, signOut, saveProfile, saveSettings }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
